import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { CenteredContent } from "../../../../../components/CenteredContent";
import { Href } from "../../../../../components/Href";
import { Icon } from "../../../../../components/Icon";
import { Z_INDEX } from "../../../../../constants/zIndex";
import { MenuMobile } from "./Menu";

/**
 * @typedef {object} Props
 * @property {boolean} isScrolled
 * @property {boolean} isHomePage
 * @property {string} backgroundColor
 */

/**
 * @param {Props} props
 */
export function HeaderMobile({ isScrolled, isHomePage, backgroundColor }) {
  const router = useRouter();
  const intl = useIntl();

  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const logoFilePath = useMemo(() => {
    if (!isHomePage) {
      return "logo.svg";
    }

    return isScrolled || isMenuOpened ? "logo.svg" : "logo-white.svg";
  }, [isHomePage, isMenuOpened, isScrolled]);

  const menuColor = useMemo(() => {
    if (!isHomePage) {
      return "brandPrimary.900";
    }

    return isScrolled || isMenuOpened ? "brandPrimary.900" : "white";
  }, [isHomePage, isMenuOpened, isScrolled]);

  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpened(false);
    }
  }, [isMobile]);

  const handleClickMenu = useCallback(() => {
    setIsMenuOpened((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      setIsMenuOpened(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("hashChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("hashChangeStart", handleRouteChange);
    };
  }, [router]);

  return (
    <Box
      display={{ base: "block", lg: "none" }}
      zIndex={Z_INDEX.HEADER_MOBILE}
      backgroundColor={isScrolled || isMenuOpened ? "white" : backgroundColor}
      boxShadow={isScrolled ? "sm" : "none"}
      px=".625rem"
      py="0.75rem">
      <CenteredContent>
        <HStack justifyContent="space-between" spacing="1rem">
          <Href href={generateFrontPath({ id: "@front.home" })}>
            <AspectRatio w="176px" ratio={176 / 60}>
              <Image
                src={generateFrontPath({
                  id: "@front.internal-assets",
                  parameters: {
                    filePath: logoFilePath,
                  },
                  includeBasePath: true,
                })}
                alt="Valentin expertises"
              />
            </AspectRatio>
          </Href>

          <IconButton
            aria-label={intl.formatMessage({ defaultMessage: "Menu" })}
            icon={
              <Icon
                icon={isMenuOpened ? "ms_close" : "ms_menu"}
                size="2rem"
                color={menuColor}
              />
            }
            variant="unstyled"
            onClick={handleClickMenu}
          />
        </HStack>
      </CenteredContent>

      <Flex
        flexDirection="column"
        role="dialog"
        aria-modal="true"
        aria-hidden={isMenuOpened ? "false" : "true"}
        tabIndex={-1}
        visibility={isMenuOpened ? "visible" : "hidden"}
        opacity={isMenuOpened ? 1 : 0}
        transition="all 0.3s"
        position="fixed"
        backgroundColor="white"
        top="72px"
        left="0"
        right="0"
        overflowY="hidden">
        {isMenuOpened && (
          <Flex
            flexGrow={1}
            flexDirection="column"
            overflowY="auto"
            p="1rem"
            borderBottom="1px solid"
            color="gray.200">
            <Box>
              <MenuMobile onClickMenu={handleClickMenu} />
            </Box>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
